/**
 * Visitor Logs — Super Admin view.
 *
 *  Platform-wide navigation, sessions and user journeys across every
 *  organisation and member. The Platform Admin's own activity is never
 *  tracked (enforced in the capture layer and the backend query).
 *
 *  Layout, top to bottom:
 *    - Header: title + "Platform Admin only" pill, a 24 Hours / 7 Days /
 *      30 Days window toggle, and CSV / XLSX / PDF exports + Retention.
 *    - KPI strip: Page Visits / Unique Visitors / Active Sessions /
 *      Average Pages / Session — all track the current window + filters.
 *    - Panels: Top Pages, By Organisation, Devices & Browsers.
 *    - Filter card: search, organisation, role, device, custom date range.
 *    - Tabs: Page Visits, Sessions, Page Analytics — each a paginated
 *      table; a row opens the full session timeline.
 *
 *  Backed by the /visitor-logs/* surface.
 */
import { useId, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { jsPDF } from 'jspdf'
import { buildXlsx } from '../../components/table/xlsx.js'
import { neutraliseFormula } from '../../components/table/tableExport.js'
import { toast } from 'sonner'
import {
  Footprints,
  ShieldCheck,
  Settings as SettingsIcon,
  Download,
  FileSpreadsheet,
  FileText,
  Eye,
  Users,
  Activity,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  HelpCircle,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { api } from '../../lib/api'
import { formatDateTime, formatNumber, todayKey } from '../../lib/format'
import {
  visitorStatsResponse,
  visitorLogsResponse,
  visitorSessionsResponse,
  visitorPageAnalyticsResponse,
  visitorRetentionResponse,
  visitorSessionDetailResponse,
  visitorExportResponse,
  platformTenantsResponse,
} from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FloatingXScrollbar } from '../../components/table/FloatingXScrollbar'
import { FilterRow } from '../../components/filters/FilterRow'
import { Modal } from '../../components/ui/Modal'
import { DONUT_PALETTE } from '../../components/charts/palette'
import { usePageSize } from '../../lib/display-prefs'

// This list's own page sizes (the visitor-logs API clamps at 200).
const VISITOR_LOG_PAGE_SIZES = [10, 25, 50, 100]

/* ----------------------------- helpers ----------------------------- */

/** Canonical combined date + time (DD/MM/YYYY HH:MM AM/PM) — the shared
 *  formatter, so timestamps read identically to every other table.
 *  Empty / invalid → em dash. */
function fmtAbs(iso) {
  return formatDateTime(iso) || '—'
}

/** Short relative reading for the "Date & Time" column. Mirrors the
 *  Login Activity house style ("sec / min / hr / days ago") so the two
 *  Logs pages read the same. */
function fmtRelative(iso) {
  if (!iso) return ''
  try {
    const ms = Date.now() - new Date(iso).valueOf()
    if (Number.isNaN(ms) || ms < 0) return ''
    const sec = Math.floor(ms / 1000)
    if (sec < 10) return 'just now'
    if (sec < 60) return `${sec} sec ago`
    const min = Math.floor(sec / 60)
    if (min < 60) return `${min} min ago`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr} hr ago`
    const day = Math.floor(hr / 24)
    if (day === 1) return 'yesterday'
    if (day < 30) return `${day} days ago`
    const mon = Math.floor(day / 30)
    if (mon < 12) return `${mon} month${mon === 1 ? '' : 's'} ago`
    const yr = Math.floor(mon / 12)
    return `${yr} year${yr === 1 ? '' : 's'} ago`
  } catch {
    return ''
  }
}

/** Human duration from milliseconds — "45 sec", "3 min 12 sec", "1 hr 4 min". */
function fmtDur(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '—'
  const sec = Math.round(ms / 1000)
  if (sec < 60) return `${sec} sec`
  const min = Math.floor(sec / 60)
  const rs = sec % 60
  if (min < 60) return rs ? `${min} min ${rs} sec` : `${min} min`
  const hr = Math.floor(min / 60)
  const rm = min % 60
  return rm ? `${hr} hr ${rm} min` : `${hr} hr`
}

const titleCase = (s) =>
  s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : ''

function roleLabel(role) {
  if (role === 'company_admin') return 'Company Admin'
  if (role === 'sub_admin') return 'Sub Admin'
  if (role === 'user') return 'User'
  if (role === 'super_admin') return 'Platform Admin'
  return titleCase(role) || 'Unknown'
}

const ROLE_PILL = {
  company_admin: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
  sub_admin: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  user: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
  super_admin: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10',
}

function RolePill({ role }) {
  const cls = ROLE_PILL[role] ?? ROLE_PILL.user
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${cls}`}>
      {roleLabel(role)}
    </span>
  )
}

const EVENT_META = {
  session_start: { label: 'Session start', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10' },
  page_view: { label: 'Page view', cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10' },
  session_end: { label: 'Session end', cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10' },
}

function EventBadge({ type }) {
  const m = EVENT_META[type] ?? { label: titleCase(type), cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  )
}

/** Device glyph — declared at module scope (a component chosen during
 *  render would trip the static-components rule). */
function DeviceGlyph({ device, size = 14, className = '' }) {
  if (device === 'mobile') return <Smartphone size={size} className={className} />
  if (device === 'tablet') return <Tablet size={size} className={className} />
  if (device === 'desktop') return <Monitor size={size} className={className} />
  return <HelpCircle size={size} className={className} />
}

/** Device · Browser cell with a leading device glyph. */
function DeviceCell({ device, browser }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[color:var(--color-ink-muted)]">
      <DeviceGlyph device={device} size={14} className="shrink-0" />
      <span>{titleCase(device)}{browser ? ` · ${browser}` : ''}</span>
    </span>
  )
}

/** IP + country cell with a location pin. */
function LocationCell({ ip, country }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[color:var(--color-ink-muted)] whitespace-nowrap">
      <MapPin size={13} className="shrink-0 text-[color:var(--color-ink-subtle)]" />
      <span>{ip || '—'}</span>
      {country ? <span className="text-[color:var(--color-ink-subtle)]">· {country}</span> : null}
    </span>
  )
}

/** Absolute date over a relative reading, for the "Date & Time" column. */
function WhenCell({ iso }) {
  return (
    <div className="whitespace-nowrap">
      <div className="text-[color:var(--color-ink)] tabular-nums">{fmtAbs(iso)}</div>
      <div className="text-xs text-[color:var(--color-ink-subtle)]">{fmtRelative(iso)}</div>
    </div>
  )
}

/* ------------------------------- page ------------------------------- */

const EMPTY_STATS = {
  totals: { totalEvents: 0, pageViews: 0, uniqueUsers: 0, uniqueSessions: 0, activeSessions: 0 },
  byRole: [],
  byDevice: [],
  topBrowsers: [],
  topCountries: [],
  topPages: [],
  byOrganization: [],
  eventsPerDay: [],
}

const RANGE_PRESETS = [
  { key: '24h', label: '24 Hours', ms: 24 * 60 * 60 * 1000 },
  { key: '7d', label: '7 Days', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: '30 Days', ms: 30 * 24 * 60 * 60 * 1000 },
]

const TABS = [
  { key: 'visits', label: 'Page Visits' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'analytics', label: 'Page Analytics' },
]

/** Resolve the effective from/to for the current window. Called only at
 *  fetch time (never during render) so Date.now() stays out of render —
 *  a live preset ("24 Hours") re-anchors on each refetch. */
function resolveWindow({ rangePreset, from, to }) {
  if (rangePreset === 'custom') {
    return { from: from || undefined, to: to ? `${to}T23:59:59.999` : undefined }
  }
  const p = RANGE_PRESETS.find((x) => x.key === rangePreset) ?? RANGE_PRESETS[1]
  return { from: new Date(Date.now() - p.ms).toISOString(), to: undefined }
}

export function VisitorLogsPage() {
  // Scroll container for the wide table — drives the floating horizontal
  // scrollbar pinned to the viewport bottom.
  const tableScrollRef = useRef(null)
  const authStatus = useAuthStore((s) => s.status)
  const isAuthed = authStatus === 'authenticated'
  const queryClient = useQueryClient()

  const [rangePreset, setRangePreset] = useState('7d') // '24h' | '7d' | '30d' | 'custom'
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [role, setRole] = useState('')
  const [device, setDevice] = useState('')
  const [tab, setTab] = useState('visits')
  const [page, setPage] = useState(0)
  // Starts at the account rows-per-page within this list's own sizes
  // (10/25/50/100; the visitor-logs API clamps at 200).
  const [pageSize, setPageSize] = usePageSize({ options: VISITOR_LOG_PAGE_SIZES })

  const [sessionId, setSessionId] = useState(null)
  const [retentionOpen, setRetentionOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Non-date filters (pure). The date window is resolved at fetch time via
  // resolveWindow() so Date.now() never runs during render.
  const baseFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      tenantId: tenantId || undefined,
      role: role || undefined,
      device: device || undefined,
    }),
    [search, tenantId, role, device],
  )

  // Stable identity for query keys — the window INPUTS, not the resolved
  // timestamp, so a live preset doesn't churn the cache every render.
  const filterKey = useMemo(
    () => ({ ...baseFilters, rangePreset, from, to }),
    [baseFilters, rangePreset, from, to],
  )

  const periodLabel =
    rangePreset === 'custom'
      ? 'Custom'
      : (RANGE_PRESETS.find((x) => x.key === rangePreset)?.label ?? '7 Days')

  const hasAnyFilter =
    search.trim() !== '' || tenantId !== '' || role !== '' || device !== '' ||
    rangePreset !== '7d' || from !== '' || to !== ''

  function selectPreset(key) {
    setPage(0)
    setRangePreset(key)
  }
  function resetPageAnd(setter) {
    return (val) => {
      setPage(0)
      setter(val)
    }
  }
  function setFromDate(val) {
    setPage(0)
    setFrom(val)
    setRangePreset('custom')
  }
  function setToDate(val) {
    setPage(0)
    setTo(val)
    setRangePreset('custom')
  }
  function switchTab(key) {
    setPage(0)
    setTab(key)
  }
  function clearFilters() {
    setPage(0)
    setSearch('')
    setTenantId('')
    setRole('')
    setDevice('')
    setFrom('')
    setTo('')
    setRangePreset('7d')
  }

  /* ------- queries ------- */

  const statsQuery = useQuery({
    queryKey: ['visitor-logs', 'stats', filterKey],
    enabled: isAuthed,
    refetchInterval: isAuthed ? 60_000 : false,
    // Keep the previous stats on screen while a new filter fetches —
    // the same placeholderData the table queries below use. Without
    // it, every filter change (a date pick, even a search keystroke)
    // emptied the KPI cards + Top Pages / By Organisation panels to
    // their empty states, the content above the filter card shrank by
    // hundreds of pixels, and the browser's scroll clamp dumped the
    // user into the middle of the table before the panels regrew
    // (QA-reported jump).
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = { ...baseFilters, ...resolveWindow({ rangePreset, from, to }) }
      const res = await api.get('/visitor-logs/stats', { params })
      return visitorStatsResponse.parse(res.data)
    },
  })

  // The Organisation dropdown lists COMPANIES, not the analytics
  // breakdown. `stats.byOrganization` is a top-10 chart aggregation
  // (`$sort count desc` + `$limit 10` in visitor-logs.service.js), so
  // driving the filter from it capped the dropdown at the ten busiest
  // organisations and hid every quieter one — including any company with
  // no visitor logs at all (QA, 2026-09-01).
  //
  // Same request the Audit Logs company filter makes, and it shares its
  // query key so the two pages reuse one cached list. Sending no `page`
  // is deliberate: `Tenants.list` only paginates when `page` is present,
  // so this returns every company rather than the first 25.
  const tenantsQuery = useQuery({
    queryKey: ['platform-tenants-dropdown'],
    enabled: isAuthed,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants')
      return platformTenantsResponse.parse(res.data)
    },
  })

  // Unfiltered facets — still needed for organisations that appear ONLY in
  // the logs (deleted since their events were recorded), so their history
  // stays filterable even though they're gone from the tenant list.
  const facetsQuery = useQuery({
    queryKey: ['visitor-logs', 'facets'],
    enabled: isAuthed,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/visitor-logs/stats')
      return visitorStatsResponse.parse(res.data)
    },
  })

  const retentionQuery = useQuery({
    queryKey: ['visitor-logs', 'retention'],
    enabled: isAuthed,
    queryFn: async () => {
      const res = await api.get('/visitor-logs/settings')
      return visitorRetentionResponse.parse(res.data)
    },
  })

  const visitsQuery = useQuery({
    queryKey: ['visitor-logs', 'visits', filterKey, page, pageSize],
    enabled: isAuthed && tab === 'visits',
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = { ...baseFilters, ...resolveWindow({ rangePreset, from, to }), eventType: 'page_view', page, limit: pageSize }
      const res = await api.get('/visitor-logs', { params })
      return visitorLogsResponse.parse(res.data)
    },
  })

  const sessionsQuery = useQuery({
    queryKey: ['visitor-logs', 'sessions', filterKey, page, pageSize],
    enabled: isAuthed && tab === 'sessions',
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = { ...baseFilters, ...resolveWindow({ rangePreset, from, to }), page, limit: pageSize }
      const res = await api.get('/visitor-logs/sessions', { params })
      return visitorSessionsResponse.parse(res.data)
    },
  })

  const analyticsQuery = useQuery({
    queryKey: ['visitor-logs', 'analytics', filterKey, page, pageSize],
    enabled: isAuthed && tab === 'analytics',
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const params = { ...baseFilters, ...resolveWindow({ rangePreset, from, to }), page, limit: pageSize }
      const res = await api.get('/visitor-logs/page-analytics', { params })
      return visitorPageAnalyticsResponse.parse(res.data)
    },
  })

  const sessionQuery = useQuery({
    queryKey: ['visitor-logs', 'session', sessionId],
    enabled: isAuthed && Boolean(sessionId),
    queryFn: async () => {
      const res = await api.get(`/visitor-logs/session/${encodeURIComponent(sessionId)}`)
      return visitorSessionDetailResponse.parse(res.data)
    },
  })

  const stats = statsQuery.data ?? EMPTY_STATS
  const totals = stats.totals
  const facets = facetsQuery.data ?? stats
  // Numerator = page views that belong to a counted session (blank
  // sessionIds are excluded from BOTH sides — dividing raw pageViews
  // by session-only denominators inflated the figure; QA-reported).
  // Falls back to raw pageViews for older API payloads.
  const avgPagesPerSession =
    totals.uniqueSessions > 0
      ? (totals.sessionPageViews ?? totals.pageViews) / totals.uniqueSessions
      : 0

  const orgOptions = useMemo(() => {
    const live = (tenantsQuery.data?.data ?? []).map((t) => ({
      value: t.id,
      label: t.name,
    }))
    const known = new Set(live.map((o) => o.value))
    // Organisations that exist only in the logs — deleted after their
    // events were recorded. `byOrganization` labels these from the
    // ingest-time name snapshot, so keep them rather than orphaning
    // their history behind an un-selectable filter.
    const historical = facets.byOrganization
      .filter((o) => o.tenantId && !known.has(o.tenantId))
      .map((o) => ({ value: o.tenantId, label: o.tenantName }))
    return [
      { value: '', label: 'All companies' },
      ...[...live, ...historical].sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    ]
  }, [tenantsQuery.data, facets.byOrganization])

  const activePagination =
    tab === 'visits'
      ? visitsQuery.data?.pagination
      : tab === 'sessions'
        ? sessionsQuery.data?.pagination
        : analyticsQuery.data?.pagination
  const pagination = activePagination ?? { page: 0, limit: pageSize, total: 0, totalPages: 0 }
  const tableFetching =
    tab === 'visits' ? visitsQuery.isFetching : tab === 'sessions' ? sessionsQuery.isFetching : analyticsQuery.isFetching

  /* ------- retention mutation ------- */

  const retentionMutation = useMutation({
    mutationFn: async (retentionDays) => {
      const res = await api.patch('/visitor-logs/settings', { retentionDays })
      return visitorRetentionResponse.parse(res.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitor-logs', 'retention'] })
      toast.success('Retention period updated successfully.')
      setRetentionOpen(false)
    },
    onError: () => toast.error('Could not update the retention period.'),
  })

  /* ------- export ------- */

  async function fetchExportRows() {
    const params = { ...baseFilters, ...resolveWindow({ rangePreset, from, to }) }
    const res = await api.get('/visitor-logs/export', { params })
    const data = visitorExportResponse.parse(res.data)
    if (data.truncated) {
      toast.warning('Export capped at 50,000 rows — narrow the filters for a complete export.')
    }
    return data.rows
  }

  async function runExport(kind) {
    if (exporting) return
    setExporting(true)
    try {
      const rowsOut = await fetchExportRows()
      if (rowsOut.length === 0) {
        toast.info('There is nothing to export for these filters.')
        return
      }
      // CSV / XLSX get the same human-readable timestamp the app's
      // tables show (fmtAbs → canonical DD/MM/YYYY HH:MM AM/PM in the
      // display timezone). The API ships Time as a raw ISO-UTC string,
      // which opened in Excel as "2026-07-14T05:32:00.199Z" — wrong-
      // looking wall time AND unreadable (QA-reported). The PDF keeps
      // the raw rows: its Time column formats via its own `fmt`, and
      // pre-formatting here would double-format to an invalid date.
      const displayRows = rowsOut.map((r) => ({
        ...r,
        Time: r.Time ? fmtAbs(r.Time) : '',
      }))
      if (kind === 'csv') exportCsv(displayRows)
      else if (kind === 'xlsx') exportXlsx(displayRows)
      else buildVisitorPdf(rowsOut)
    } catch {
      toast.error('Could not export the visitor logs.')
    } finally {
      setExporting(false)
    }
  }

  const shownFrom = pagination.total === 0 ? 0 : page * pageSize + 1
  const shownTo = Math.min((page + 1) * pageSize, pagination.total)

  function changePageSize(n) {
    setPage(0)
    setPageSize(n)
  }

  /* ------------------------------ render ------------------------------ */

  return (
    <div>
      <Breadcrumb current="Visitor Logs" homePath="/super-admin/dashboard" />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3 flex-wrap">
            <Footprints size={22} className="text-[color:var(--color-accent-strong)]" />
            Visitor Logs
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
              <ShieldCheck size={12} />
              Platform Admin only
            </span>
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Platform-wide navigation, sessions and user journeys across every company and member.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RangeToggle value={rangePreset} onChange={selectPreset} />
          <button type="button" onClick={() => setRetentionOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]">
            <SettingsIcon size={14} /> Retention
          </button>
          <button type="button" onClick={() => runExport('csv')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50">
            <Download size={14} /> CSV
          </button>
          <button type="button" onClick={() => runExport('xlsx')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50">
            <FileSpreadsheet size={14} /> XLSX
          </button>
          <button type="button" onClick={() => runExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50">
            <FileText size={14} /> PDF
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Page Visits" value={totals.pageViews} icon={Eye} tone="emerald" loading={statsQuery.isLoading} />
        <KpiCard label="Unique Visitors" value={totals.uniqueUsers} icon={Users} tone="sky" loading={statsQuery.isLoading} />
        <KpiCard label="Active Sessions" value={totals.activeSessions} icon={Activity} tone="amber" loading={statsQuery.isLoading} />
        <KpiCard label="Average Pages / Session" value={avgPagesPerSession} icon={Clock} tone="violet" loading={statsQuery.isLoading} />
      </div>

      {/* Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel title="Top Pages" badge={periodLabel}>
          <RankList rows={stats.topPages.map((p) => ({ label: p.path, value: p.count }))} emptyMessage="No page visits yet." />
        </Panel>
        <Panel title="By Company">
          <RankList rows={stats.byOrganization.map((o) => ({ label: o.tenantName, value: o.count }))} emptyMessage="No company activity yet." />
        </Panel>
        <Panel title="Devices &amp; Browsers">
          <DevicesBrowsers byDevice={stats.byDevice} topBrowsers={stats.topBrowsers} />
        </Panel>
      </div>

      {/* Filter card */}
      <FilterRow>
        <FilterRow.Search value={search} onChange={resetPageAnd(setSearch)} placeholder="Search by Name, Email ID, IP Address or Page" />
        <FilterRow.Select ariaLabel="Company" placeholder="All companies" value={tenantId} onChange={resetPageAnd(setTenantId)} options={orgOptions} />
        <FilterRow.Select
          ariaLabel="Role"
          placeholder="All roles"
          value={role}
          onChange={resetPageAnd(setRole)}
          options={[
            { value: '', label: 'All roles' },
            { value: 'company_admin', label: 'Company Admin' },
            { value: 'sub_admin', label: 'Sub Admin' },
            { value: 'user', label: 'User' },
          ]}
        />
        <FilterRow.Select
          ariaLabel="Device"
          placeholder="All devices"
          value={device}
          onChange={resetPageAnd(setDevice)}
          options={[
            { value: '', label: 'All devices' },
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' },
            { value: 'tablet', label: 'Tablet' },
            { value: 'unknown', label: 'Unknown' },
          ]}
        />
        <FilterRow.DateField label="From" value={from} onChange={setFromDate} max={to || undefined} />
        <FilterRow.DateField label="To" value={to} onChange={setToDate} min={from || undefined} />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
      </FilterRow>

      {/* Tabs */}
      <div className="mt-4 border-b border-[color:var(--color-rule)] flex items-center gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => switchTab(t.key)}
            className={
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ' +
              (tab === t.key
                ? 'border-[color:var(--color-accent)] text-[color:var(--color-accent-strong)]'
                : 'border-transparent text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-[color:var(--color-card)] border border-[color:var(--color-rule)] border-t-0 rounded-b-2xl overflow-hidden">
        {/* `data-fd-xscroll` opts this wide table into the floating horizontal
         *  scrollbar below — the same affordance DataTable gives every other
         *  admin table, so you don't have to scroll to the bottom of a long
         *  list to reach the native bar. Purely additive: no layout change. */}
        <div ref={tableScrollRef} data-fd-xscroll className="overflow-x-auto">
          {tab === 'visits' ? (
            <VisitsTable query={visitsQuery} hasAnyFilter={hasAnyFilter} onOpenSession={setSessionId} />
          ) : tab === 'sessions' ? (
            <SessionsTable query={sessionsQuery} hasAnyFilter={hasAnyFilter} onOpenSession={setSessionId} />
          ) : (
            <AnalyticsTable query={analyticsQuery} hasAnyFilter={hasAnyFilter} />
          )}
        </div>
        <FloatingXScrollbar containerRef={tableScrollRef} />

        {/* Pagination */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-muted)] flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2">
              <span>Rows</span>
              <select
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                aria-label="Rows per page"
                className="h-8 pl-2 pr-6 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:ring-1 focus:ring-[color:var(--color-accent)]"
              >
                {VISITOR_LOG_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <span className="tabular-nums">
              {pagination.total === 0 ? 'No results' : `Showing ${shownFrom}–${shownTo} of ${formatNumber(pagination.total)}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || tableFetching} aria-label="Previous page" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] disabled:opacity-40 hover:bg-[color:var(--color-card-hover)]">
              <ChevronLeft size={16} />
            </button>
            <span className="tabular-nums">{pagination.totalPages === 0 ? 0 : page + 1} / {pagination.totalPages}</span>
            <button type="button" onClick={() => setPage((p) => (p + 1 < pagination.totalPages ? p + 1 : p))} disabled={page + 1 >= pagination.totalPages || tableFetching} aria-label="Next page" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] disabled:opacity-40 hover:bg-[color:var(--color-card-hover)]">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Session timeline modal */}
      <Modal open={Boolean(sessionId)} onClose={() => setSessionId(null)} title="Session Timeline" size="lg" dismissOnBackdrop>
        {sessionQuery.isLoading ? (
          <p className="py-10 text-center text-sm text-[color:var(--color-ink-muted)]">Loading session…</p>
        ) : sessionQuery.isError || !sessionQuery.data ? (
          <p className="py-10 text-center text-sm text-[color:var(--color-priority-urgent)]">Could not load this session.</p>
        ) : (
          <SessionView detail={sessionQuery.data} />
        )}
      </Modal>

      {/* Retention settings modal — keyed on open so it remounts (and
       *  re-seeds the field from the current setting) each time. */}
      <RetentionModal
        key={retentionOpen ? 'retention-open' : 'retention-closed'}
        open={retentionOpen}
        onClose={() => setRetentionOpen(false)}
        current={retentionQuery.data}
        saving={retentionMutation.isPending}
        onSave={(days) => retentionMutation.mutate(days)}
      />
    </div>
  )
}

/* ---------------------------- tab tables ---------------------------- */

function VisitsTable({ query, hasAnyFilter, onOpenSession }) {
  const rows = query.data?.logs ?? []
  return (
    <table className="w-full text-left text-sm min-w-[1080px]">
      <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)]">
        <tr>
          <Th>Visitor</Th>
          <Th>Company</Th>
          <Th>Role</Th>
          <Th>Page</Th>
          <Th>IP / Location</Th>
          <Th>Device · Browser</Th>
          {/* "Date & Time" (was "When") — QA asked for an explicit
           *  label; the cell shows the absolute timestamp over a
           *  relative reading. */}
          <Th>Date &amp; Time</Th>
        </tr>
      </thead>
      <tbody>
        {query.isLoading ? (
          <StateRow cols={7}>Loading…</StateRow>
        ) : query.isError ? (
          <StateRow cols={7} error>Could not load page visits.</StateRow>
        ) : rows.length === 0 ? (
          <StateRow cols={7}>{hasAnyFilter ? 'No page visits match these filters.' : 'No page visits yet.'}</StateRow>
        ) : (
          rows.map((r) => (
            <tr
              key={r._id}
              onClick={() => r.sessionId && onOpenSession(r.sessionId)}
              className={'border-b border-[color:var(--color-rule)] last:border-b-0 ' + (r.sessionId ? 'cursor-pointer hover:bg-[color:var(--color-card-hover)]' : '')}
            >
              <Td><VisitorCell name={r.userName} email={r.userEmail} /></Td>
              <Td className="whitespace-nowrap text-[color:var(--color-ink)] truncate max-w-[160px]">{r.tenantName}</Td>
              <Td><RolePill role={r.role} /></Td>
              <Td className="max-w-[240px]"><span className="text-sm text-[color:var(--color-ink-muted)] truncate block" title={r.pageTitle || r.path}>{r.path || '—'}</span></Td>
              <Td><LocationCell ip={r.ip} country={r.geo?.country} /></Td>
              <Td className="whitespace-nowrap"><DeviceCell device={r.device} browser={r.browser} /></Td>
              <Td><WhenCell iso={r.createdAt} /></Td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function SessionsTable({ query, hasAnyFilter, onOpenSession }) {
  const rows = query.data?.sessions ?? []
  return (
    <table className="w-full text-left text-sm min-w-[1040px]">
      <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)]">
        <tr>
          <Th>Visitor</Th>
          <Th>Company</Th>
          <Th className="text-right">Pages</Th>
          <Th>Duration</Th>
          <Th>Device · Browser</Th>
          <Th>Started</Th>
          <Th>Last Seen</Th>
          <Th className="text-right"><span className="sr-only">Actions</span></Th>
        </tr>
      </thead>
      <tbody>
        {query.isLoading ? (
          <StateRow cols={8}>Loading…</StateRow>
        ) : query.isError ? (
          <StateRow cols={8} error>Could not load sessions.</StateRow>
        ) : rows.length === 0 ? (
          <StateRow cols={8}>{hasAnyFilter ? 'No sessions match these filters.' : 'No sessions yet.'}</StateRow>
        ) : (
          rows.map((s) => (
            <tr
              key={s.sessionId}
              onClick={() => onOpenSession(s.sessionId)}
              className="border-b border-[color:var(--color-rule)] last:border-b-0 cursor-pointer hover:bg-[color:var(--color-card-hover)]"
            >
              <Td><VisitorCell name={s.userName} email={s.userEmail} /></Td>
              <Td className="whitespace-nowrap text-[color:var(--color-ink)] truncate max-w-[160px]">{s.tenantName}</Td>
              <Td className="text-right tabular-nums text-[color:var(--color-ink-strong)] font-medium">{s.pageViewCount}</Td>
              <Td className="whitespace-nowrap text-[color:var(--color-ink-muted)]">{fmtDur(s.durationMs)}</Td>
              <Td className="whitespace-nowrap"><DeviceCell device={s.device} browser={s.browser} /></Td>
              <Td className="whitespace-nowrap text-[color:var(--color-ink)] tabular-nums">{fmtAbs(s.startedAt)}</Td>
              <Td><WhenCell iso={s.endedAt} /></Td>
              <Td className="text-right whitespace-nowrap">
                <span className="text-[color:var(--color-accent-strong)] font-medium">View →</span>
              </Td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function AnalyticsTable({ query, hasAnyFilter }) {
  const rows = query.data?.pages ?? []
  return (
    <table className="w-full text-left text-sm min-w-[820px]">
      <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)]">
        <tr>
          <Th>Page</Th>
          <Th className="text-right">Views</Th>
          <Th className="text-right">Unique Visitors</Th>
          <Th className="text-right">Average Time</Th>
          <Th>Last Visit</Th>
        </tr>
      </thead>
      <tbody>
        {query.isLoading ? (
          <StateRow cols={5}>Loading…</StateRow>
        ) : query.isError ? (
          <StateRow cols={5} error>Could not load page analytics.</StateRow>
        ) : rows.length === 0 ? (
          <StateRow cols={5}>{hasAnyFilter ? 'No pages match these filters.' : 'No page views yet.'}</StateRow>
        ) : (
          rows.map((p) => (
            <tr key={p.path} className="border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-card-hover)]">
              <Td className="max-w-[420px]"><span className="text-sm text-[color:var(--color-ink)] truncate block" title={p.path}>{p.path || '—'}</span></Td>
              <Td className="text-right tabular-nums text-[color:var(--color-ink-strong)] font-semibold">{formatNumber(p.views)}</Td>
              <Td className="text-right tabular-nums text-[color:var(--color-accent-strong)] font-medium">{formatNumber(p.uniqueUsers)}</Td>
              <Td className="text-right tabular-nums text-[color:var(--color-ink-muted)]">{fmtDur(p.avgTimeMs)}</Td>
              <Td className="whitespace-nowrap text-[color:var(--color-ink-muted)]">{fmtAbs(p.lastVisited)}</Td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function VisitorCell({ name, email }) {
  return (
    <div className="min-w-0">
      <div className="font-medium text-[color:var(--color-ink-strong)] truncate">{name || '—'}</div>
      <div className="text-xs text-[color:var(--color-ink-muted)] truncate">{email}</div>
    </div>
  )
}

function StateRow({ cols, error, children }) {
  return (
    <tr>
      <Td colSpan={cols} className={'text-center py-10 ' + (error ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-muted)]')}>
        {children}
      </Td>
    </tr>
  )
}

/* --------------------------- presentational -------------------------- */

function RangeToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-[color:var(--color-rule)] overflow-hidden">
      {RANGE_PRESETS.map((p, i) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(p.key)}
          className={
            'px-3 py-2 text-sm font-medium transition-colors ' +
            (i > 0 ? 'border-l border-[color:var(--color-rule)] ' : '') +
            (value === p.key
              ? 'bg-[color:var(--color-accent)] text-white'
              : 'bg-[color:var(--color-card)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
          }
        >
          {p.label}
        </button>
      ))}
    </div>
  )
}

function Panel({ title, badge, children, className = '' }) {
  return (
    <section className={`bg-[color:var(--color-card)] border border-[color:var(--color-rule)] rounded-2xl p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium">{title}</h2>
        {badge ? <span className="text-xs text-[color:var(--color-ink-subtle)] font-medium">{badge}</span> : null}
      </div>
      {children}
    </section>
  )
}

/** Simple ranked bar list — label, proportional bar, count. */
function RankList({ rows, emptyMessage = 'No data yet.' }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">{emptyMessage}</p>
  }
  const max = Math.max(1, ...rows.map((r) => r.value ?? 0))
  return (
    <ul className="space-y-3">
      {rows.slice(0, 8).map((r, i) => (
        <li key={r.label + '_' + i}>
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-sm text-[color:var(--color-ink)] truncate" title={r.label}>{r.label}</span>
            <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0">{formatNumber(r.value ?? 0)}</span>
          </div>
          <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, (r.value / max) * 100)}%`, backgroundColor: DONUT_PALETTE[i % DONUT_PALETTE.length] }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Devices (with share %) over browser chips. */
function DevicesBrowsers({ byDevice, topBrowsers }) {
  const deviceTotal = byDevice.reduce((a, d) => a + d.count, 0)
  if (deviceTotal === 0) {
    return <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">No device data yet.</p>
  }
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {byDevice.map((d) => {
          const pct = Math.round((d.count / deviceTotal) * 100)
          return (
            <div key={d.device}>
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center gap-1.5 text-sm text-[color:var(--color-ink)]">
                  <DeviceGlyph device={d.device} size={14} /> {titleCase(d.device)}
                </span>
                <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)' }} />
              </div>
            </div>
          )
        })}
      </div>
      {topBrowsers.length > 0 ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {topBrowsers.map((b) => (
            <span key={b.browser} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-[color:var(--color-ink)]">
              {b.browser}
              <span className="text-[color:var(--color-ink-muted)] tabular-nums">{b.count}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function Th({ children, className = '' }) {
  return <th className={'px-4 py-3 font-medium text-sm ' + className}>{children}</th>
}
function Td({ children, className = '', colSpan }) {
  return <td colSpan={colSpan} className={'px-4 py-3 align-middle ' + className}>{children}</td>
}

/* --------------------------- session detail -------------------------- */

function EventTimeline({ events }) {
  if (!events || events.length === 0) {
    return <p className="text-sm text-[color:var(--color-ink-muted)] italic">No events recorded.</p>
  }
  return (
    <ol className="relative border-l border-[color:var(--color-rule)] ml-2 space-y-4">
      {events.map((e) => (
        <li key={e._id} className="ml-4">
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[color:var(--color-accent)]" />
          <div className="flex items-center gap-2 flex-wrap">
            <EventBadge type={e.eventType} />
            <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">{fmtAbs(e.createdAt)}</span>
            {e.durationMs != null ? <span className="text-xs text-[color:var(--color-ink-subtle)]">· {fmtDur(e.durationMs)}</span> : null}
          </div>
          {e.path ? <div className="mt-1 text-sm text-[color:var(--color-ink)] truncate" title={e.pageTitle || e.path}>{e.path}</div> : null}
        </li>
      ))}
    </ol>
  )
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wider text-[color:var(--color-ink-subtle)]">{label}</dt>
      <dd className="text-sm text-[color:var(--color-ink)] mt-0.5">{value || '—'}</dd>
    </div>
  )
}

function SessionView({ detail }) {
  const s = detail.summary
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-medium text-[color:var(--color-ink-strong)]">{s.user?.name || '—'}</div>
        <div className="text-xs text-[color:var(--color-ink-muted)]">{s.user?.email} · {roleLabel(s.user?.role)} · {s.tenantName}</div>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <DetailRow label="Started" value={fmtAbs(s.startedAt)} />
        <DetailRow label="Ended" value={fmtAbs(s.endedAt)} />
        <DetailRow label="Duration" value={fmtDur(s.durationMs)} />
        <DetailRow label="Page Views" value={String(s.pageViewCount)} />
        <DetailRow label="Entry Page" value={s.entryPage} />
        <DetailRow label="Exit Page" value={s.exitPage} />
        <DetailRow label="Device" value={`${titleCase(s.device)}${s.browser ? ` · ${s.browser}` : ''}${s.os ? ` · ${s.os}` : ''}`} />
        <DetailRow label="Location" value={s.country || '—'} />
        <DetailRow label="IP Address" value={s.ip} />
      </dl>
      <div>
        <h3 className="text-sm font-medium text-[color:var(--color-ink-strong)] mb-3">Timeline</h3>
        <EventTimeline events={detail.events} />
      </div>
    </div>
  )
}

// Remounted on open (via `key` in the parent) so the field seeds once from
// the current setting and an empty string stays a real, editable state —
// deriving the value from the seed on every empty render would make the
// field impossible to clear.
function RetentionModal({ open, onClose, current, saving, onSave }) {
  const [days, setDays] = useState(() =>
    current?.retentionDays != null ? String(current.retentionDays) : '',
  )
  const [error, setError] = useState(null)
  const uid = useId()
  const daysId = `${uid}-days`
  const errorId = `${uid}-days-error`

  function submit(e) {
    e.preventDefault()
    // Number() + isInteger (not parseInt) so "12.7" is rejected rather
    // than silently truncated. Bounds mirror the backend DTO (0–365,
    // 0 = keep forever; QA ruled the old 3650 cap down to one year).
    const n = Number(days)
    if (days.trim() === '' || !Number.isInteger(n) || n < 0 || n > 365) {
      setError(
        'Enter a whole number of days between 0 and 365. Use 0 to keep logs forever.',
      )
      return
    }
    setError(null)
    onSave(n)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Visitor Log Retention"
      description="Choose how long to keep visitor logs. Older entries are purged automatically once a day."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-md border border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]">Cancel</button>
          <button type="submit" form="retention-form" disabled={saving} className="px-3 py-2 rounded-md bg-[color:var(--color-accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      }
    >
      {/* `noValidate` — without it the browser's native constraint
       *  validation (min/max on the number input) intercepted the
       *  submit and showed its own per-browser bubble ("Please select
       *  a value that is no less than 0."), so the app-level check in
       *  submit() never ran (QA-reported). The min/max attributes stay
       *  for the stepper arrows; validation itself is ours, rendered
       *  as the standard inline role="alert" field error. */}
      <form id="retention-form" onSubmit={submit} noValidate className="space-y-3">
        {/* Unwrapped from a `<label>` that wrapped the input. A wrapping
         *  label makes its WHOLE box a click target, so the empty space
         *  beside the caption, the gap above the input and the error
         *  paragraph below it all focused the field — clicking above the
         *  textbox started accepting input (QA). Explicit `htmlFor` plus a
         *  `w-fit` caption keeps the association while shrinking the hit
         *  target to the visible text, as FieldLabel does. */}
        <div>
          <label
            htmlFor={daysId}
            className="block w-fit text-sm font-medium text-[color:var(--color-ink)] mb-1"
          >
            Retention period (days)
          </label>
          <input
            id={daysId}
            aria-describedby={error ? errorId : undefined}
            type="number"
            min={0}
            max={365}
            value={days}
            onChange={(e) => {
              setDays(e.target.value)
              if (error) setError(null)
            }}
            aria-invalid={error ? true : undefined}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:ring-1 focus:ring-[color:var(--color-accent)] focus:border-[color:var(--color-accent)] ' +
              (error
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)]')
            }
          />
          {error ? (
            <p id={errorId} role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {error}
            </p>
          ) : null}
        </div>
        <p className="text-xs text-[color:var(--color-ink-muted)]">Set to <strong>0</strong> to keep logs forever.</p>
        {current?.lastPurgeAt ? <p className="text-xs text-[color:var(--color-ink-subtle)]">Last purge: {fmtAbs(current.lastPurgeAt)}</p> : null}
      </form>
    </Modal>
  )
}

/* ------------------------------ exports ------------------------------ */

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function exportCsv(rows) {
  const headers = Object.keys(rows[0])
  // RFC-4180 field encoding: quote a value ONLY when it contains a comma,
  // double-quote, or newline, and double any embedded quote. The previous
  // `JSON.stringify` escaping was WRONG — it emitted backslash escapes (`\"`,
  // `\n`) that Excel and RFC-4180 parsers mis-read, so a value with a quote
  // (e.g. a page title) closed its field early and the rest spilled into the
  // next columns. Matches the shared table exporter (components/table/
  // tableExport.js `csvField`).
  const esc = (v) => {
    // Page, referrer and user-agent are typed by an anonymous VISITOR —
    // neutralise formula injection before a Super Admin opens this in Excel.
    const s = neutraliseFormula(v == null ? '' : String(v))
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const lines = [headers.map(esc).join(',')]
  for (const r of rows) lines.push(headers.map((h) => esc(r[h])).join(','))
  // UTF-8 BOM so Excel opens the file with the right encoding (its default
  // codepage on Windows is cp1252 — without the BOM, em-dashes and accented
  // characters render as gibberish). `\r\n` row terminators per RFC-4180.
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, `visitor-logs-${todayKey()}.csv`)
}

// Uses the app's own dependency-free .xlsx encoder (the one every other table
// export already goes through) instead of the `xlsx` npm package, which is
// stuck on 0.18.5 with unfixed prototype-pollution / ReDoS advisories. Same
// header order as `exportCsv` above (keys of the first row).
function exportXlsx(rows) {
  const headers = Object.keys(rows[0])
  const blob = buildXlsx(
    headers,
    rows.map((r) => headers.map((h) => r[h])),
    'Visitor Logs',
  )
  triggerDownload(blob, `visitor-logs-${todayKey()}.xlsx`)
}

/** Compact tabular PDF (landscape A4). jsPDF lays out text only, so a
 *  focused column subset keeps it legible. */
function buildVisitorPdf(rows) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape', compress: true })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 32
  let y = margin

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(17, 17, 17)
  doc.text('Visitor Logs', margin, y)
  y += 16
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(120, 120, 120)
  doc.text(`Generated ${formatDateTime(new Date())} · ${formatNumber(rows.length)} rows`, margin, y)
  y += 16

  const cols = [
    { key: 'Time', label: 'Date and Time', w: 108, fmt: (v) => (v ? fmtAbs(v) : '—') },
    { key: 'User', w: 96 },
    { key: 'Role', w: 74 },
    { key: 'Company', w: 96 },
    { key: 'Event', w: 66 },
    { key: 'Path', w: 150 },
    { key: 'Device', w: 56 },
    { key: 'Browser', w: 60 },
    { key: 'Country', w: 48 },
  ]

  function header() {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(60, 60, 60)
    let x = margin
    for (const c of cols) {
      doc.text(c.label ?? c.key, x, y)
      x += c.w
    }
    y += 6
    doc.setDrawColor(210)
    doc.setLineWidth(0.6)
    doc.line(margin, y, pageW - margin, y)
    y += 10
  }

  header()
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(40, 40, 40)

  for (const r of rows) {
    if (y > pageH - margin) {
      doc.addPage()
      y = margin
      header()
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(40, 40, 40)
    }
    let x = margin
    doc.setFontSize(7.5)
    for (const c of cols) {
      const raw = c.fmt ? c.fmt(r[c.key]) : r[c.key]
      const line = doc.splitTextToSize(String(raw ?? ''), c.w - 4)[0] ?? ''
      doc.text(line, x, y)
      x += c.w
    }
    y += 12
  }

  const pages = doc.getNumberOfPages()
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(150, 150, 150)
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 16, { align: 'right' })
  }

  doc.save(`visitor-logs-${todayKey()}.pdf`)
}

/* ------------------------------- KPI -------------------------------- */

const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#E0F2FE] text-[#0284C7] border border-[#BAE6FD] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#E0F2FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
    halo: 'from-[#FEF3C7] to-transparent dark:from-transparent',
  },
}

function KpiCard({ label, value, icon: Icon, tone = 'violet', loading }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div aria-hidden="true" className={'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' + tones.halo} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">{label}</h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0), { maximumFractionDigits: 1 })}
            </p>
          )}
        </div>
        <div className={'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' + tones.tile}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

export default VisitorLogsPage
